import { Box, Stack, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import type { ReactElement } from 'react';

import type { StatusResponse } from '@/types/status';

export interface StatusFooterProps {
  status: StatusResponse | undefined;
  variant?: 'expanded' | 'collapsed';
}

type DotState = 'good' | 'warn' | 'bad' | 'unknown';

function dotForAge(ageSeconds: number | null): DotState {
  if (ageSeconds === null) return 'unknown';
  if (ageSeconds < 60) return 'good';
  if (ageSeconds < 300) return 'warn';
  return 'bad';
}

function dotForBurn(percent: number): DotState {
  if (percent >= 90) return 'bad';
  if (percent >= 70) return 'warn';
  return 'good';
}

function ageLabel(ageSeconds: number | null): string {
  if (ageSeconds === null) return '—';
  if (ageSeconds < 60) return `${ageSeconds}s`;
  if (ageSeconds < 3600) return `${Math.floor(ageSeconds / 60)}m`;
  return `${Math.floor(ageSeconds / 3600)}h`;
}

function Dot({
  state,
  breathe = false,
}: {
  state: DotState;
  breathe?: boolean;
}): ReactElement {
  const theme = useTheme();
  const colorMap: Record<DotState, string> = {
    good: theme.palette.success.main,
    warn: theme.palette.warning.main,
    bad: theme.palette.error.main,
    unknown: theme.palette.text.disabled,
  };
  const ring: Partial<Record<DotState, string>> = {
    good: 'rgba(107, 255, 171, 0.5)',
    warn: 'rgba(255, 176, 0, 0.5)',
    bad: 'rgba(239, 68, 68, 0.5)',
  };
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-block',
        width: 6,
        height: 6,
        borderRadius: '50%',
        flex: 'none',
        backgroundColor: colorMap[state],
        boxShadow: ring[state] ? `0 0 0 1px ${ring[state]}` : 'none',
        animation: breathe && state === 'good' ? 'breathe 2.4s ease-in-out infinite' : 'none',
      }}
    />
  );
}

export function StatusFooter({
  status,
  variant = 'expanded',
}: StatusFooterProps): ReactElement {
  const sched = status?.scheduler;
  const dd = status?.dd_sync;
  const llm = status?.llm_burn;

  const schedDot = sched ? dotForAge(sched.age_seconds) : 'unknown';
  const ddDot = dd ? dotForAge(dd.age_seconds) : 'unknown';
  const burnDot = llm ? dotForBurn(llm.percent) : 'unknown';

  if (variant === 'collapsed') {
    return (
      <Stack
        direction="column"
        spacing={1}
        alignItems="center"
        sx={{ py: 1.5, borderTop: 1, borderColor: 'divider' }}
        aria-label="System status"
      >
        <Dot state={schedDot} breathe />
        <Dot state={ddDot} breathe />
        <Dot state={burnDot} />
      </Stack>
    );
  }

  return (
    <Box sx={{ p: 1.5, borderTop: 1, borderColor: 'divider' }} aria-label="System status">
      <Typography
        variant="caption"
        sx={{ display: 'block', mb: 0.75, color: 'text.faintest' }}
      >
        status
      </Typography>

      <Stack
        direction="row"
        alignItems="center"
        spacing={1}
        sx={{ py: 0.25 }}
        title={
          sched?.last_tick_at
            ? `scheduler last tick ${ageLabel(sched.age_seconds)} ago`
            : 'scheduler — no data'
        }
      >
        <Dot state={schedDot} breathe />
        <Box sx={{ flex: 1, color: 'text.secondary', fontSize: 11 }}>scheduler</Box>
        <Box sx={{ color: 'text.primary', fontSize: 11 }} className="tnum">
          {ageLabel(sched?.age_seconds ?? null)}
        </Box>
      </Stack>

      <Stack
        direction="row"
        alignItems="center"
        spacing={1}
        sx={{ py: 0.25 }}
        title={
          dd?.last_sync_at ? `dd sync ${ageLabel(dd.age_seconds)} ago` : 'dd sync — no data'
        }
      >
        <Dot state={ddDot} breathe />
        <Box sx={{ flex: 1, color: 'text.secondary', fontSize: 11 }}>dd sync</Box>
        <Box sx={{ color: 'text.primary', fontSize: 11 }} className="tnum">
          {ageLabel(dd?.age_seconds ?? null)}
        </Box>
      </Stack>

      <Box sx={{ pt: 0.75 }}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ pb: 0.5 }}>
          <Dot state={burnDot} />
          <Box sx={{ flex: 1, color: 'text.secondary', fontSize: 11 }}>llm burn</Box>
          <Box sx={{ color: 'text.primary', fontSize: 11 }} className="tnum">
            {llm ? `${llm.percent.toFixed(0)}%` : '—'}
          </Box>
        </Stack>
        <Box
          sx={{
            height: 4,
            borderRadius: 2,
            backgroundColor: 'background.elev2',
            overflow: 'hidden',
          }}
          aria-hidden="true"
        >
          <Box
            sx={{
              height: '100%',
              width: `${Math.min(llm?.percent ?? 0, 100)}%`,
              background:
                burnDot === 'bad'
                  ? 'linear-gradient(90deg, #ef4444, #ff6b6b)'
                  : 'linear-gradient(90deg, #D97757, #f0a37c)',
              boxShadow: 'none',
              transition: 'width 1.4s cubic-bezier(0.25, 1, 0.5, 1)',
            }}
          />
        </Box>
        <Stack direction="row" justifyContent="space-between" sx={{ mt: 0.5 }}>
          <Box sx={{ color: 'text.faintest', fontSize: 10 }} className="tnum">
            ${(llm?.spent_usd ?? 0).toFixed(2)}
          </Box>
          <Box sx={{ color: 'text.faintest', fontSize: 10 }} className="tnum">
            ${(llm?.budget_usd ?? 0).toFixed(2)}
          </Box>
        </Stack>
      </Box>
    </Box>
  );
}
